/**
 * CalendarColorPicker Component
 * Dropdown popover for selecting calendar colors from a preset palette or custom hex input.
 * Used by CalendarConnectionItem and CalendarSelectorDialog.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Check, X } from 'lucide-react';
import { CALENDAR_PRESET_COLORS } from '@signalvote/shared';
import { cn } from '@/lib/utils';

// ============================================================================
// Types
// ============================================================================

interface CalendarColorPickerProps {
  /** Currently selected color hex */
  selectedColor: string | null;
  /** Set of color hex strings already in use (will be disabled) */
  usedColors: Set<string>;
  /** Called when user selects a color */
  onColorSelect: (color: string) => void;
  /** Called when user resets to default (null) */
  onReset?: () => void;
  /** Whether the picker is disabled */
  disabled?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

const HEX_REGEX = /^#[0-9a-fA-F]{6}$/;

function isValidHex(value: string): boolean {
  return HEX_REGEX.test(value);
}

// ============================================================================
// Component
// ============================================================================

export function CalendarColorPicker({
  selectedColor,
  usedColors,
  onColorSelect,
  onReset,
  disabled = false,
}: CalendarColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customHex, setCustomHex] = useState('');
  const [customError, setCustomError] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowCustomInput(false);
        setCustomError('');
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus custom input when shown
  useEffect(() => {
    if (showCustomInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showCustomInput]);

  const handlePresetClick = useCallback(
    (hex: string) => {
      if (usedColors.has(hex.toLowerCase()) && hex.toLowerCase() !== selectedColor?.toLowerCase()) {
        return; // Can't select an in-use color (unless it's our own current color)
      }
      onColorSelect(hex);
      setIsOpen(false);
      setShowCustomInput(false);
    },
    [usedColors, selectedColor, onColorSelect]
  );

  const handleCustomSubmit = useCallback(() => {
    const hex = customHex.startsWith('#') ? customHex : `#${customHex}`;
    if (!isValidHex(hex)) {
      setCustomError('Enter a valid hex (e.g. #d50000)');
      return;
    }
    if (usedColors.has(hex.toLowerCase()) && hex.toLowerCase() !== selectedColor?.toLowerCase()) {
      setCustomError('This color is already in use');
      return;
    }
    onColorSelect(hex.toLowerCase());
    setIsOpen(false);
    setShowCustomInput(false);
    setCustomHex('');
    setCustomError('');
  }, [customHex, usedColors, selectedColor, onColorSelect]);

  const handleReset = useCallback(() => {
    onReset?.();
    setIsOpen(false);
    setShowCustomInput(false);
  }, [onReset]);

  const displayColor = selectedColor || '#9ca3af';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger: Color dot */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'h-5 w-5 rounded-full border-2 border-white shadow-sm',
          'transition-transform hover:scale-110',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        style={{ backgroundColor: displayColor }}
        title="Change color"
      />

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute left-0 top-full mt-2 z-50 w-56 rounded-lg border bg-popover p-3 shadow-lg">
          {/* Preset Grid */}
          <div className="grid grid-cols-8 gap-1.5">
            {CALENDAR_PRESET_COLORS.map(({ name, hex }) => {
              const isUsed = usedColors.has(hex.toLowerCase()) && hex.toLowerCase() !== selectedColor?.toLowerCase();
              const isSelected = hex.toLowerCase() === selectedColor?.toLowerCase();
              return (
                <button
                  key={hex}
                  type="button"
                  onClick={() => handlePresetClick(hex)}
                  disabled={isUsed}
                  className={cn(
                    'h-5 w-5 rounded-full transition-all',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                    isUsed
                      ? 'opacity-25 cursor-not-allowed'
                      : 'cursor-pointer hover:scale-125 hover:shadow-md',
                    isSelected && 'ring-2 ring-foreground ring-offset-1'
                  )}
                  style={{ backgroundColor: hex }}
                  title={isUsed ? `${name} (in use)` : name}
                >
                  {isSelected && (
                    <Check className="h-3 w-3 text-white mx-auto" strokeWidth={3} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Divider + Actions */}
          <div className="mt-3 flex items-center gap-2 border-t pt-2">
            {!showCustomInput ? (
              <>
                <button
                  type="button"
                  onClick={() => setShowCustomInput(true)}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Custom hex...
                </button>
                {onReset && selectedColor && (
                  <button
                    type="button"
                    onClick={handleReset}
                    className="ml-auto text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Reset
                  </button>
                )}
              </>
            ) : (
              <div className="w-full">
                <div className="flex items-center gap-1.5">
                  <input
                    ref={inputRef}
                    type="text"
                    value={customHex}
                    onChange={(e) => {
                      setCustomHex(e.target.value);
                      setCustomError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCustomSubmit();
                      if (e.key === 'Escape') {
                        setShowCustomInput(false);
                        setCustomError('');
                      }
                    }}
                    placeholder="#d50000"
                    className={cn(
                      'flex-1 h-7 rounded border px-2 text-xs',
                      'focus:outline-none focus:ring-1 focus:ring-ring',
                      customError ? 'border-destructive' : 'border-input'
                    )}
                    maxLength={7}
                  />
                  <button
                    type="button"
                    onClick={handleCustomSubmit}
                    className="h-7 w-7 flex items-center justify-center rounded bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustomInput(false);
                      setCustomError('');
                    }}
                    className="h-7 w-7 flex items-center justify-center rounded hover:bg-accent text-muted-foreground"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                {customError && (
                  <p className="mt-1 text-[0.65rem] text-destructive">{customError}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarColorPicker;
